import React, { useState, useEffect, useContext } from 'react'
import { contactLabel, companyLabel } from "../utils/recordLabel"
import { RELATION_VALUE, relationValueCell } from "../utils/relationValue"
import clsx from 'clsx'
import Image from './Image'
import DurationCounterView from './DurationCounterView'
import formatValue from "../utils/formatValue"
import Skeleton from '@material-ui/lab/Skeleton'
import {
  Box as MuiBox,
  Chip,
  useTheme,
  CircularProgress,
  Table as MuiTable,
  TableBody as MuiTableBody,
  TableCell as MuiTableCell,
  TableHead as MuiTableHead,
  TableRow as MuiTableRow,
  IconButton as MuiIconButton,
  Collapse,
  Checkbox,
} from '@material-ui/core'
import {
  KeyboardArrowUp,
  KeyboardArrowDown,
  DoubleArrow as PriorityIcon,
  PriorityHighRounded as CriticalIcon,
  Block as BlockerIcon,
} from '@material-ui/icons'
import useStyles from './styles'
import RichText from './RichText'
import { useAppContext } from 'App'
import { getStyleProgressStatus, getCustomTextStyle } from 'shared/utils'
import Text from './Text'
import OpenConversationRows from './OpenConversationRows'
import FollowupRows from './FollowupRows'
import moment from 'moment'
import FileListView from './FileListView'
import FileNameList from './FileNameList'
import { fileEntries, extensionOf, fileKind } from 'shared/utils/fileType'
import { FileTypeTile } from './FileTypeIcon'
import { AvatarInitial } from './AvatarInitial'

const getPriorityStyle = (key) => {
  let style = {}
  const base = { display: 'inline-block', position: 'relative' }

  if (key === 'high') {
    style = { ...base, color: '#E57373', transform: 'rotate(270deg)' }
  } else if (key === 'low') {
    style = { ...base, color: 'green', transform: 'rotate(90deg)', marginLeft: -2, marginRight: 4 }
  } else if (key === 'trivial') {
    style = { ...base, color: 'grey', transform: 'rotate(90deg)', marginLeft: -2, marginRight: 4 }
  } else if (['blocker', 'critical'].includes(key)) {
    style = { ...base, color: '#B71C1C', marginRight: 4 }
  }
  // else if (['blocker'].includes(key)) {
  //   style = { ...base, color: 'red', marginRight: 4 }
  // } 
  else {
    style = { ...base, top: 4, color: 'rgb(255, 179, 0)', marginRight: 2 }
  }

  return style
}

const TableCell = (props) => {
  const classes = useStyles()
  const theme = useTheme()
  // const {column, header, style, value, secondaryValue, onClick} = props
  const { column, header, getValue, onClick, stickyCell = false, stickyCellRight = false, isSelected, useCollapse = false } = props

  let align = column.alignment || 'left'
  if (column.type === 'number' || column.type == 'currency' || column.type == 'decimal')
    align = 'right'
  let customStyle = {}

  customStyle["padding"] = theme.spacing(2)

  if (column.wordWrap === false || column.type == 'currency')
    customStyle['white-space'] = 'nowrap'

  if (column.width) {
    customStyle['width'] = column.width
    customStyle['flexShrink'] = false
  } else if (column?.primary?.width) {
    customStyle['min-width'] = column.primary.width
    customStyle['max-width'] = column.primary.width
    customStyle['flexShrink'] = false
    if (column.wordWrap === false) customStyle['white-space'] = 'unset'
  }

  if (stickyCell || stickyCellRight) {
    // Above whatever the other cells scrolling underneath paint: a positioned child there (an avatar, a chip) is stacked after
    // this cell in the DOM and, with this cell at z-index 0, painted right over it. A sticky header sits above the other header
    // cells (MUI's sticky header is z-index 2), or those slide over it; body cells stay below the header row.
    customStyle['zIndex'] = header ? 3 : 1
  }
  if (!stickyCell && header) {
    // MUI's stickyHeader also pins every header cell to `left: 0`, so each one would stop at the left edge (over the sticky
    // column's header) when the table scrolls sideways; only the sticky column's header (left: 0 by its class) pins there
    customStyle['left'] = 'auto'
  }

  if (useCollapse) {
    customStyle['border'] = 0
  }

  customStyle = { ...customStyle, ...(column.valueStyle || {}) }

  if (header)
    return (
      <MuiTableCell
        className={clsx(classes.tableCellHeader, stickyCell && classes.tableCellSticky, stickyCellRight && classes.tableCellStickyRight)}
        key={column.key}
        align={align}
        style={customStyle}>
        {column.name}
      </MuiTableCell>
    )

  const media = getValue("media")
  const primary = getValue("primary")
  const secondary = getValue("secondary")
  const third = getValue("third")

  return (
    <MuiTableCell 
      className={clsx(classes.tableCell, stickyCell && classes.tableCellSticky, stickyCellRight && classes.tableCellStickyRight, isSelected && classes.tableCellItemSelected)} 
      key={column.key} 
      align={align} 
      style={customStyle}
      onClick={onClick}
    >
      {
        isSelected &&
        <div className={classes.wrapItemSelected}>
          <div className={classes.itemSelected}/>
        </div>
      }
      {media ?
        <MuiBox display='flex' alignItems='flex-start'>
          {media}
          <MuiBox ml={2} flexGrow={1}>
            <div className={classes.primaryText}>
              {primary}
            </div>
            {secondary &&
              <>
                <MuiBox mt={.2} />
                <div className={classes.secondaryText}>
                  {secondary}
                </div>
              </>
            }
            {third &&
              <>
                <MuiBox mt={.2} />
                <div className={classes.thirdText}>
                  {third}
                </div>
              </>
            }
          </MuiBox>
        </MuiBox>
        :
        <>
          <div className={classes.primaryText}>
            {primary}
          </div>
          {secondary &&
            <>
              <MuiBox mt={.2} />
              <div className={classes.secondaryText}>
                {secondary}
              </div>
            </>
          }
          {third &&
            <>
              <MuiBox mt={.2} />
              <div className={classes.thirdText}>
                {third}
              </div>
            </>
          }
        </>
      }
    </MuiTableCell>
  )
}

export const Table = ({
  itemListRef, columns, rows, showCheckBox, showIndicatorSelected = false, skeleton, isFetching, hasMore, onRowClick,
  onRowSelect, isRowSelected, showColumnHeader = true, disabled = false, stickyColumn = false, stickyColumnIndex = 0, stickyColumnIndexRight = -1,
  columnHeaderStyle = {}, containerRef, onLoadMore, ...props
}) => {
  const classes = useStyles()
  const theme = useTheme()
  const appContext = useAppContext()

  /** destructure collapse props */
  const { useCollapse = false, renderCollapseItem = null } = props

  /** declare state for collapse items */
  const [showCollapseIdx, setShowCollapseIdx] = useState(-1)

  /** scroll listener */
  useEffect(() => {
    const container = containerRef?.current
    if (!container || !onLoadMore) return

    const handleScroll = () => {
      /** check if scrolled to bottom */
      if (
        container.scrollTop + container.clientHeight >= container.scrollHeight - 1
      ) {
        onLoadMore()
      }
    }

    container.addEventListener('scroll', handleScroll);
    return () => {
      container.removeEventListener('scroll', handleScroll)
    }
  }, [containerRef, onLoadMore])

  const getUserName = contactLabel

  const getValue = (row, column, fieldKey) => {
    const field = column[fieldKey] || {}

    if (skeleton) {
      if (field.render || field.key) {
        if (fieldKey == "media")
          return (
            <MuiBox style={{
              width: field.width || '10rem',
            }}>
              <Skeleton
                variant="rect"
                width={field.width || '10rem'}
                height={field.width || '10rem'}
              />
            </MuiBox>
          )

        return <Skeleton />
      }
      return null
    }


    // `{ component: 'OpenConversations', max?: 3 }` as a column's primary / secondary / third (nothing when none open)
    if (field.component === 'OpenConversations')
      return <OpenConversationRows item={row} max={field.max} compact />

    // `{ component: 'Followup' }` as a column's primary / secondary / third (nothing without an active follow-up)
    if (field.component === 'Followup')
      return <FollowupRows item={row} compact />

    // The document view's rows (one per file, the file's props on the row itself): `{ field_type: 'file_icon' }` as a column's
    // `media` is the file's type icon tile (a picture for an image), `{ field_type: 'file_ext' }` the file's extension (PDF, DOCX…)
    if (field.field_type === 'file_icon') {
      const name = row.file_name || row.filename
      const url = row.image_url || row.media_url
      return <FileTypeTile name={name} url={url} thumbnailUrl={fileKind(name, url) === 'image' && extensionOf(name || url) !== 'heic' ? url : undefined} />
    }
    if (field.field_type === 'file_ext')
      return (extensionOf(row.file_name || row.filename) || extensionOf(row.image_url || row.media_url)).toUpperCase() || null

    if (field.render) {
      if (field.variant == 'title')
        return (
          <span className={classes.titleText}>
            {field.render(row, column)}
          </span>
        )

      return field.render(row, column)
    }

    if (props.getCellValue)
      return props.getCellValue(row, column, fieldKey)

    /** value assignment conditions */
    let value
    if (field.getValue)
      value = field.getValue(row, column)
    else if(field.field_type == RELATION_VALUE)
      value = relationValueCell(row, field)
    else if(field.field_type == 'contact')
      value = getUserName(row[field.key])
    else if(field.field_type == 'company')
      value = companyLabel(row[field.key])
    else if(field?.field_type == 'company_contact')
      value = `${getUserName(row[field.key]?.contact)} ${row[field.key]?.company ? `from ${row[field.key]?.company?.name}` : ''}`
    else if(field.field_type == 'option'){
      if (field?.multiple_value) {
        value = row[field.key]?.map(field => <Chip label={field.name} style={{margin: '3px 5px 3px 0'}}/>)
      } else value = row[field.key]?.name
    }
    else if (field?.field_type == "issue") {
      if (row[field.key]) {
        const items = Array.isArray(row[field.key]) ? row[field.key] : [row[field.key]]
        value = items.map(field => <Chip label={field.key} style={{margin: '3px 5px 3px 0'}}/>)
      } else {
        value = null
      }
    }
    else if(field.field_type == 'custom' && field?.key == 'esb_outlet'){
      value = row?.[field.key]?.name || row?.[field.key]?.title || row?.[field.key]?.value
    }
    else if(field.field_type == 'user'){
      let name = ''
      if (row[field.key]) {
        if (row[field.key].uid == appContext.userInfo?.uid)
          name = "Me"
        else
          name = `${row[field.key].first_name || ''} ${row[field.key].last_name || ''}`

        value = (
          <div className={classes.assigneeNameCol}>
            <AvatarInitial user={row[field.key]} size="s" />
            <Text>{name}</Text>
          </div>
        )
      } else {
        // Assigned to all agent
        value = "Unassigned"
      }
    }
    else if(field.field_type == 'status'){
      value = (
        <div style={{ display: 'flex' }}>
          <MuiBox>
            <Text
              variant="body2"
              noWrap={true}
              textCustomProps={getStyleProgressStatus(row[field.key])}
            >
              {row[field.key]?.name}
            </Text>
          </MuiBox>
        </div>
      )
    }
    else if (field.field_type == 'priority') {
      let priorityName = row[field.key]?.name
      let priorityKey =  row[field.key]?.key
      value = (
        <span style={{ display: 'inline-flex', alignItems: 'baseline' }}>
          {['normal', 'low', 'high', 'trivial'].includes(priorityKey) && (
            <div style={getPriorityStyle(priorityKey)}>
              <PriorityIcon fontSize="inherit" />
            </div>
          )}
          {priorityKey === 'critical' && (
            <div style={{ ...getPriorityStyle(priorityKey), fontSize: '0.7rem' }}>
              ‼️
            </div>
          )}
          {priorityKey === 'blocker' && (
            <div style={{ ...getPriorityStyle(priorityKey), fontSize: '0.9rem' }}>
              🚫
            </div>
          )}
          {priorityName}
        </span>
      )
    }
    else if (field.field_type == 'timer') {
      value = (
        <div>
          {row[field.key]?.map((t, index) => {
            return <DurationCounterView key={index} t={t} />
          })}
        </div>
      )
    }
    else if(field.field_type == 'float')
      value = row[field.key] ? new Intl.NumberFormat('ID').format(row[field.key]) : ''
    else if(
      field.field_type == 'date' ||
      (moment(row[field.key], moment.ISO_8601, true).isValid() && !field.format) // check value is date without format
    )
      value = formatValue(row[field.key], 'datetime|DD MMM yyy')
    else if (field?.field_type == 'media')
      value = row[field.key] ? (
        <FileListView value={row[field.key]} useSmallThumbs disableBorder />
      ) : <></>
    // the media field's files as "file icon + file name" lines instead of thumbnails
    else if (field?.field_type == 'file')
      value = fileEntries(row[field.key]).length > 0 ? <FileNameList value={row[field.key]} /> : null
    else
      value = formatValue(row[field.key], field.format)

    /** return value conditions */
    if (value === null || value === undefined || value === "")
      return null
    else if (typeof value === "function")
      return value
    else if (fieldKey == "media")
      return (
        <MuiBox style={{
          width: field.width || '10rem',
        }}
        >
          <Image src={value} aspectRatio={column.field} />
        </MuiBox>
      )
    /*
    else if (column.type == 'button')
      valueComponent = (
        <MuiBox display='flex' flexDirection='row'>
          {value}
        </MuiBox>
      )
    */
    else if (field.variant == 'title')
      return (
        <span className={classes.titleText}>
          {value}
        </span>
      )
    else if ((field.bg_color || field.color) && ['string', 'number'].includes(typeof value) && field.field_type !== 'status')
      return (
        <div style={{ display: 'flex' }}>
          <MuiBox>
            <Text
              variant="body2"
              noWrap={true}
              textCustomProps={getCustomTextStyle(field?.color, field?.bg_color, value)}
            >
              {value}
            </Text>
          </MuiBox>
        </div>
      )
    else
      return value
  }

  const getCellValue = (row, column, rowIndex, columnIndex) => {
    if (column.primary)
      return getCellValue(row, column.primary, rowIndex, columnIndex)

    if (column.render)
      return column.render(row, column, rowIndex, columnIndex)
    if (props.getCellValue)
      return props.getCellValue(row, column, rowIndex, columnIndex)

    return formatValue(row[column.key], column.format)
  }

  const getCellSecondaryValue = (row, column, rowIndex, columnIndex) => {
    if (column.secondary)
      return getCellValue(row, column.secondary, rowIndex, columnIndex)

    if (props.getCellSecondaryValue)
      return props.getCellSecondaryValue(row, column)
    return null
  }

  const getCellStyle = (row, column, rowIndex, columnIndex) => {
    if (props.getCellStyle)
      return props.getCellStyle(row, column, rowIndex, columnIndex)
    return null
  }

  const handleOnRowClick = (row, rowIndex) => {
    if (onRowClick) {
      onRowClick(row, rowIndex)
    } else if (!onRowClick && useCollapse) {
      setShowCollapseIdx(showCollapseIdx === rowIndex ? -1 : rowIndex)
    }
  }

  const isDisableCheckbox = (row, rowIndex) => props?.isRowCheckBoxDisabled && props?.isRowCheckBoxDisabled(row, rowIndex)
  const isHeaderCheckboxDisabled = Boolean(props?.isHeaderCheckboxDisabled) || !props?.onSelectAllRows
  const isHeaderCheckboxChecked = Boolean(props?.isAllRowsSelected)
  const isHeaderCheckboxIndeterminate = Boolean(props?.isSomeRowsSelected)

  return (
    <div className={clsx(classes.table)}>
      <MuiTable stickyHeader {...props}>
        {showColumnHeader &&
          <MuiTableHead>
            <MuiTableRow>
              {showCheckBox &&
                <MuiTableCell className={classes.tableCellHeaderCheckBox} style={columnHeaderStyle}>
                  <Checkbox
                    indeterminate={isHeaderCheckboxIndeterminate}
                    checked={isHeaderCheckboxChecked}
                    disabled={isHeaderCheckboxDisabled}
                    onChange={() => {
                      if (props?.onSelectAllRows) props.onSelectAllRows()
                    }}
                  />
                </MuiTableCell>
              }
              {useCollapse && (
                // <MuiTableCell className={classes.tableCellHeaderCollapse} style={columnHeaderStyle} />
                <TableCell header={true} theme={theme} classes={classes} column={{ width: 1 }} />
              )}
              {columns.map(c => ({ ...c, valueStyle: { ...columnHeaderStyle } })).map((column, columnIndex) => (
                <TableCell
                  header={true}
                  stickyCell={stickyColumn && stickyColumnIndex == columnIndex}
                  stickyCellRight={stickyColumn && stickyColumnIndexRight == columnIndex}
                  theme={theme}
                  classes={classes}
                  column={column}
                />
              ))}
            </MuiTableRow>
          </MuiTableHead>
        }
        <MuiTableBody>
          {skeleton
            ? [...Array(skeleton)].map((_, rowIndex) => (
              <MuiTableRow key={rowIndex}>
                {columns.map((column, columnIndex) => (
                  <TableCell theme={theme} classes={classes}
                    column={column}
                    getValue={(fieldKey) => getValue(undefined, column, fieldKey)}
                  />
                ))}
              </MuiTableRow>
            ))
            : rows && rows.map((row, rowIndex) => {
              return (
                <React.Fragment key={row.code}>
                  <MuiTableRow hover selected={!showCheckBox && isRowSelected && isRowSelected(row, rowIndex)} tabIndex={-1} key={row.code} ref={ref => {if(itemListRef) itemListRef.current[rowIndex] = ref}}>
                    {showCheckBox &&
                      <MuiTableCell
                        className={classes.tableCellCheckBox}
                        // onClick={() => typeof props?.isRowCheckBoxDisabled !== 'undefined' ? 
                        //   !isDisableCheckbox(row, rowIndex) && onRowSelect && onRowSelect(row, rowIndex) : 
                        //   onRowSelect && onRowSelect(row, rowIndex)}
                        style={ useCollapse ? { border: 0 } : {}}
                      >
                        <Checkbox
                          checked={isRowSelected && isRowSelected(row, rowIndex)}
                          onChange={(event) => typeof props?.isRowCheckBoxDisabled !== 'undefined' ? 
                            !isDisableCheckbox(row, rowIndex) && onRowSelect && onRowSelect(row, rowIndex) : 
                            onRowSelect && onRowSelect(row, rowIndex)}
                        />
                      </MuiTableCell>
                    }
                    {useCollapse && (
                      <MuiTableCell className={classes.tableCellCollapse} style={{ border: 0 }}>
                        <MuiIconButton
                          aria-label="expand row"
                          size="small"
                          onClick={() => setShowCollapseIdx(showCollapseIdx === rowIndex ? -1 : rowIndex)}
                        >
                          {showCollapseIdx === rowIndex ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                        </MuiIconButton>
                      </MuiTableCell>
                    )}
                    {columns.map((column, columnIndex) => (
                      <TableCell theme={theme} classes={classes}
                        stickyCell={stickyColumn && stickyColumnIndex == columnIndex}
                        stickyCellRight={stickyColumn && stickyColumnIndexRight == columnIndex}
                        column={column}
                        getValue={(fieldKey) => getValue(row, column, fieldKey)}
                        onClick={() => handleOnRowClick(row, rowIndex)}
                        useCollapse={useCollapse}
                      />
                    ))}
                  </MuiTableRow>
                  {useCollapse && (
                    <MuiTableRow>
                      <MuiTableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={12}>
                        <Collapse in={showCollapseIdx === rowIndex} timeout="auto" unmountOnExit>
                          <MuiBox margin={1}>
                            {renderCollapseItem(row, rowIndex)}
                          </MuiBox>
                        </Collapse>
                      </MuiTableCell>
                    </MuiTableRow>
                  )}
                </React.Fragment>
              );
            })
          }
        </MuiTableBody>
      </MuiTable>

      {isFetching &&
        <MuiBox display='flex' justifyContent='center' style={{ padding: theme.spacing(2) }}>
          <CircularProgress size='1.5rem' disableShrink={true} />
        </MuiBox>
      }
    </div>
  )
}

export default Table